import { clients, HttpError } from 'lib/https';
import {
  IConversationByIdPayload,
  IConversationByIdResponse,
  IConversationInitiatePayload,
  IConversationInitiateResponse,
  IConversationListPayload,
  IConversationListResponse,
} from '../types/conversation.service.type';
import { getConfigurationSettings } from 'services/configuration-service';

export class ConversationService {
  async initiate(
    payload: IConversationInitiatePayload,
    blocksKey: string
  ): Promise<IConversationInitiateResponse> {
    const config = await getConfigurationSettings();
    const url = `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.conversationInitiate}`;
    const body = (sessionId?: string) =>
      JSON.stringify({
        widget_id: payload.widget_id,
        project_key: payload.project_key,
        session_id: sessionId,
      });

    try {
      return await clients.post(url, blocksKey, body(payload.session_id));
    } catch (err) {
      // BE returns 400 when a previously stored session_id has expired its
      // 30-min Redis TTL. Retry once with an empty session_id so the BE
      // mints a fresh one instead of surfacing the failure to the user.
      if (
        payload.session_id &&
        err instanceof HttpError &&
        err.status === 400
      ) {
        return await clients.post(url, blocksKey, body(''));
      }
      throw err;
    }
  }

  async getConversationList(
    payload: IConversationListPayload,
    blocksKey: string
  ): Promise<IConversationListResponse> {
    // Admin "AI Agents > Conversations" view: the cross-user (agentAdmin) list of this
    // agent's playground/test runs (real chats live in the sidebar), with the page's
    // user/date filters. test defaults to true; the playground filter can narrow it.
    const config = await getConfigurationSettings();
    const body = {
      agentId: payload.agent_id,
      agentAdmin: true,
      page: Math.max(1, payload.offset ?? 1),
      pageSize: payload.limit ?? 10,
      isArchived: false,
      withLastUserMessage: true,
      isPrivate: payload.is_private,
      test: payload.pg ?? true,
      searchQuery: payload.query ?? undefined,
      startDate: payload.start_date ?? undefined,
      endDate: payload.end_date ?? undefined,
      userId: payload.user_id ?? undefined,
      userEmail: payload.user_email ?? undefined,
    };
    const res = (await clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.collaborationConversation}`,
      blocksKey,
      JSON.stringify(body)
    )) as { data?: any[]; totalCount?: number };
    const list = res?.data ?? [];
    const sessions = list.map((conv: any) => ({
      agent_id: payload.agent_id,
      widget_id: '',
      session_id: conv.conversationId,
      created_at: conv.createdDate,
      last_entry_date: conv.lastUpdatedDate,
      user_name: conv.userName,
      conversation: {
        Title: conv.conversationTitle,
        // last user message (withLastUserMessage) as snippet fallback when there's no title
        Response: conv.messages?.[0]?.content,
        UserId: conv.createdBy,
        Playground: !!conv.test,
      },
    }));
    return {
      sessions: sessions as unknown as IConversationListResponse['sessions'],
      total_count: res?.totalCount ?? sessions.length,
    };
  }

  async getConversationSessionById(
    payload: IConversationByIdPayload,
    blocksKey: string
  ): Promise<IConversationByIdResponse> {
    // Open one agent conversation from the unified collaboration store and pair the flat
    // message list into the Query/Response turns the admin box renders. agentAdmin grants
    // the owner/admin cross-user read so another user's test chat isn't empty.
    const config = await getConfigurationSettings();
    const res = (await clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.getCollaborationMessages}`,
      blocksKey,
      JSON.stringify({
        conversationId: payload.session_id,
        agentId: payload.agent_id,
        agentAdmin: true,
        pageSize: 500,
      })
    )) as { data?: any[]; totalCount?: number };
    const messages = (res?.data ?? []).slice().sort(
      (a: any, b: any) =>
        new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
    );
    const byId = new Map(messages.map((m: any) => [m.id, m]));

    // Each agent turn -> one Query/Response session row, paired with the user message it
    // answered (via relatedUserMessageIds / parentIds).
    const sessions = messages
      .filter((m: any) => m.type === 'agent' || m.type === 'assistant')
      .map((agentMsg: any) => {
        const userMsgId =
          agentMsg.metadata?.relatedUserMessageIds?.[0] ?? agentMsg.parentIds?.[0];
        const userMsg = userMsgId ? byId.get(userMsgId) : undefined;
        const ar = agentMsg.metadata?.agentRun;
        return {
          _id: agentMsg.id,
          SessionId: payload.session_id,
          AgentId: ar?.agentId ?? agentMsg.senderId,
          Query: userMsg?.content ?? '',
          QueryId: userMsg?.id ?? '',
          Response: agentMsg.content ?? '',
          ResponseId: agentMsg.id,
          QueryTimestamp: userMsg?.createdDate ?? agentMsg.createdDate,
          ResponseTimestamp: agentMsg.createdDate,
          UserId: userMsg?.senderId ?? '',
          Metadata: agentMsg.metadata ?? {},
          Playground: !!ar?.test,
          IsPrivate: false,
        };
      });

    return {
      sessions: sessions as unknown as IConversationByIdResponse['sessions'],
      total_count: res?.totalCount ?? sessions.length,
    };
  }

  async query(payload: Record<string, unknown>, blocksKey: string) {
    const config = await getConfigurationSettings();
    const streamPayload = {
      query: payload.query,
      session_id: payload.session_id,
      base_prompt: payload.base_prompt,
      model_id: payload.model_id,
      model_name: payload.model_name,
      model_provider: payload.model_provider,
      tool_ids: payload.tool_ids,
      last_n_turns: payload.last_n_turn ?? payload.last_n_turns ?? 10,
      enable_summary: payload.enable_summary ?? true,
      enable_tools: payload.enable_tools ?? true,
      enable_next_suggestion: payload.enable_next_suggestion ?? true,
      response_type: payload.response_type,
      response_format: payload.response_format,
      response_style: payload.response_style,
      call_from: payload.call_from,
      files: payload.files,
      images: payload.images,
    };

    return clients.stream(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.aiAgentQueryStream}`,
      blocksKey,
      JSON.stringify(streamPayload),
      { Accept: 'text/event-stream' }
    );
  }

  async getLLMConversationSessionById(payload: any): Promise<any> {
    throw new Error('Legacy LLM method not implemented');
  }

  async deleteConversationSession(payload: any, blocksKey: string): Promise<any> {
    throw new Error('Legacy delete method not implemented');
  }

  async getSessions(payload: any): Promise<any> {
    throw new Error('Legacy getSessions method not implemented');
  }

  async deleteSession(payload: any): Promise<any> {
    throw new Error('Legacy deleteSession method not implemented');
  }
}

export const conversationService = new ConversationService();
